import { PermissionService } from '@supply-mover-2-0/shared';
import React from 'react';
import type {
	FooterItem,
	ModuleConfig,
	NavigationItem,
	OutletRoute,
	Route,
} from '../types/module-registry';
import { AIRegistry } from './AIRegistry';
import { SettingsRegistry } from './SettingsRegistry';

interface ModuleInfo {
	moduleConfig: ModuleConfig;
}

interface RouteInfo {
	moduleName: string;
	routeName: string;
	pattern?: RegExp;
	paramNames?: string[];
	activityDescription?: string;
}

// Route configuration for React Router. `path` is optional because index
// routes (index: true) carry no path segment of their own.
export interface RouteConfig {
	path?: string;
	element?: React.ReactElement;
	children?: RouteConfig[];
	index?: boolean;
	requiredPermissions?: Array<{ action: string }>;
}

// Join a parent path prefix and a child path segment, tolerating empty
// segments on either side (an outlet's default child has path '').
function joinRoutePaths(parentPath: string, childPath: string): string {
	if (parentPath === '') {
		return childPath;
	}
	if (childPath === '') {
		return parentPath;
	}
	return `${parentPath}/${childPath}`;
}

// Build React Router child routes for an outlet, recursing when a child is
// itself an outlet so nested layouts (e.g. /locations/:id/sensors/:sensorId
// with its own Detail / Historical Data sub-tabs) get their grandchild routes
// generated. Without this, only the first level of outlet children renders
// and deeper child paths render as a blank outlet.
//
// `pathPrefix` accumulates the segments of componentless grouping children,
// which are flattened into this outlet the same way getModuleRoutes() flattens
// top-level grouping routes. Without that flattening a grouping child under an
// outlet produced no routes at all, so its pages 404'd (AI Agents admin →
// Custom Reports).
function buildOutletChildRoutes(
	children: Route[],
	pathPrefix = '',
): RouteConfig[] {
	return children.flatMap((child) => {
		const routes: RouteConfig[] = [];
		const childPath = joinRoutePaths(pathPrefix, child.path);

		// Grouping child: no component of its own, so its children belong to
		// this outlet directly, path-prefixed with the group's segment.
		if (child.component == null && child.children != null) {
			return buildOutletChildRoutes(child.children, childPath);
		}

		// Handle default route - create index route. Only a child that resolves
		// to the outlet's own path is an index; a grouping child's default
		// (e.g. reports → '') resolves to 'reports' and is a normal route.
		if (child.default && childPath === '' && child.component != null) {
			routes.push({
				index: true,
				element: React.createElement(child.component),
				requiredPermissions: child.requiredPermissions,
			});
		}

		// Nested outlet: this child has its own outlet + children. Build a
		// regular route for it whose React Router children come from its
		// declared `children` array, processed the same way.
		if (child.outlet && child.children != null && child.component != null) {
			routes.push({
				path: childPath,
				element: React.createElement(child.component),
				children: buildOutletChildRoutes(child.children),
				requiredPermissions: child.requiredPermissions,
			});

			return routes;
		}

		// Child with a component AND its own non-outlet children (e.g. a list
		// screen that renders <Outlet /> for its detail page). Nest the children
		// under it, mirroring getModuleRoutes() for the same shape.
		if (
			child.component != null &&
			child.children != null &&
			child.outlet !== true
		) {
			routes.push({
				path: childPath,
				element: React.createElement(child.component),
				children: buildOutletChildRoutes(child.children),
				requiredPermissions: child.requiredPermissions,
			});

			return routes;
		}

		// Regular leaf route (with a path or a label so it's reachable)
		if (child.component != null && (childPath !== '' || child.label != null)) {
			routes.push({
				path: childPath,
				element: React.createElement(child.component),
				requiredPermissions: child.requiredPermissions,
			});
		}

		return routes;
	});
}

class ModuleRegistryClass {
	private readonly modules = new Map<string, ModuleInfo>();
	private readonly routeMap = new Map<string, RouteInfo>();
	private readonly outletRoutes = new Map<string, OutletRoute[]>();
	private readonly listeners = new Set<() => void>();
	private globalNavigation: NavigationItem[] | null = null;
	private initialized = false;
	private currentPath = '';

	// Convert route path to regex pattern for matching
	private pathToRegex(path: string): { pattern: RegExp; paramNames: string[] } {
		const paramNames: string[] = [];
		const pattern = path
			.replace(/\//g, '\\/')
			.replace(/:(\w+)/g, (_, paramName) => {
				paramNames.push(paramName);
				return '([^/]+)';
			})
			.replace(/\*/g, '.*');

		return {
			pattern: new RegExp(`^${pattern}$`),
			paramNames,
		};
	}

	// Build route map for quick lookups
	private buildRouteMap(
		routes: Route[],
		moduleName: string,
		parentPath = '',
	): void {
		routes.forEach((route) => {
			const fullPath = parentPath
				? route.path
					? `${parentPath}/${route.path}`
					: parentPath
				: route.path;
			const { pattern, paramNames } = this.pathToRegex(fullPath);

			// `path: ''` default children share the same fullPath as their
			// parent. If we naively overwrite, a parent with
			// `activityDescription: 'Viewed Clients'` gets clobbered by a
			// childless default-route entry (no description) and the
			// /clients page resolves to null. Preserve the existing
			// description when the new entry doesn't declare one.
			const existing = this.routeMap.get(fullPath);
			const mergedDescription =
				route.activityDescription != null && route.activityDescription !== ''
					? route.activityDescription
					: existing?.activityDescription;

			this.routeMap.set(fullPath, {
				moduleName,
				routeName: route.path,
				pattern,
				paramNames,
				activityDescription: mergedDescription,
			});

			// Process outlet routes
			if (route.outlet && route.children) {
				const outletChildren: OutletRoute[] = route.children.map((child) => {
					const childFullPath = `${fullPath}/${child.path}`;
					const { pattern: childPattern, paramNames: childParamNames } =
						this.pathToRegex(childFullPath);

					// Outlet children also need their own routeMap entry so
					// the activity-id route sync hook can find their
					// activityDescription on navigation.
					const existingChild = this.routeMap.get(childFullPath);
					const childMergedDescription =
						child.activityDescription != null &&
						child.activityDescription !== ''
							? child.activityDescription
							: existingChild?.activityDescription;
					this.routeMap.set(childFullPath, {
						moduleName,
						routeName: child.path,
						pattern: childPattern,
						paramNames: childParamNames,
						activityDescription: childMergedDescription,
					});

					return {
						path: child.path,
						component: child.component!,
						label: child.label,
						icon: child.icon,
						default: child.default,
						parentRouteName: fullPath,
						scrollable: child.scrollable,
						container: child.container,
						containerProps: child.containerProps,
						activeTab: child.activeTab,
						visible: child.visible,
						activityDescription: child.activityDescription,
						requiredPermissions: child.requiredPermissions,
					};
				});
				this.outletRoutes.set(fullPath, outletChildren);

				// Recurse into nested outlet children so their own grandchild
				// tabs register both in routeMap and in outletRoutes — required
				// when an outlet child is itself an outlet (e.g. sensors → :sensorId
				// where :sensorId has Detail / Historical Data sub-tabs).
				route.children.forEach((child) => {
					if (child.outlet && child.children) {
						this.buildRouteMap([child], moduleName, fullPath);
					}
				});
			}

			// Recursively process children
			if (route.children && !route.outlet) {
				this.buildRouteMap(route.children, moduleName, fullPath);
			}
		});
	}

	registerModule(config: ModuleConfig): void {
		if (this.modules.has(config.name)) {
			// Warning: Module is already registered. Overwriting.
		}

		// Register AI module if present
		if (config.ai) {
			AIRegistry.registerModule(config.ai);
		}

		// Register settings if present
		if (config.settings) {
			SettingsRegistry.register(config.name, config.settings);
		}

		// If this is the settings module, populate its children from SettingsRegistry
		let moduleConfig = config;
		if (config.name === 'settings') {
			const settingsRoutes = SettingsRegistry.getAllRoutesForSettings();

			// Find the /settings route and merge its children with dynamic settings routes
			moduleConfig = {
				...config,
				routes: config.routes.map((route) => {
					if (route.path === '/settings') {
						// Merge existing children with dynamic settings routes
						const existingChildren = route.children || [];
						const mergedChildren = [...existingChildren, ...settingsRoutes];
						return {
							...route,
							children: mergedChildren,
						};
					}
					return route;
				}),
			};
		}

		this.modules.set(config.name, { moduleConfig });

		// Build route map for this module
		if (moduleConfig.routes) {
			this.buildRouteMap(moduleConfig.routes, config.name);
		}

		// Notify listeners
		this.notifyListeners();
	}

	unregisterModule(name: string): void {
		if (this.modules.delete(name)) {
			// Remove routes for this module
			for (const [path, info] of this.routeMap.entries()) {
				if (info.moduleName === name) {
					this.routeMap.delete(path);
					this.outletRoutes.delete(path);
				}
			}

			// Unregister settings
			SettingsRegistry.unregister(name);

			this.notifyListeners();
		}
	}

	getModule(name: string): ModuleConfig | undefined {
		return this.modules.get(name)?.moduleConfig;
	}

	getAllModules(): ModuleConfig[] {
		return Array.from(this.modules.values()).map((info) => info.moduleConfig);
	}

	getActiveModules(): ModuleConfig[] {
		return this.getAllModules().filter((module) => module.enabled !== false);
	}

	// Get current path (for web, this would come from React Router)
	getCurrentPath(): string {
		if (typeof window !== 'undefined') {
			return window.location.pathname;
		}
		return this.currentPath;
	}

	/**
	 * Find the activityDescription that best matches the given pathname.
	 * Walks matching routes longest-first and returns the first description
	 * found, so a tab-level route without its own description naturally
	 * inherits from its parent (e.g. /clients/:id/notes inherits "Viewed
	 * Clients" from /clients when only the parent is annotated). Any
	 * {paramName} placeholders in the description are substituted with the
	 * URL-param values captured from the matched pattern — e.g. a route
	 * /clients/:id/notes annotated as "Viewed Notes for Client {id}"
	 * resolves to "Viewed Notes for Client 123" on /clients/123/notes.
	 *
	 * If no registered pattern matches the full pathname, the lookup walks
	 * up the path one segment at a time and retries. This lets sub-routes
	 * registered without a description (e.g. /settings/users from the
	 * SettingsRegistry pipeline) inherit their parent's description
	 * (/settings → "Viewed Settings") even though the parent's regex
	 * pattern is anchored and doesn't match the longer path on its own.
	 * Returns null when nothing along the chain has a description.
	 */
	getActivityDescriptionForPath(pathname: string): string | null {
		// Strip query/hash if present.
		const cleanPath = pathname.split('?')[0]?.split('#')[0] ?? pathname;

		let candidate = cleanPath;
		while (candidate.length > 0) {
			const found = this._lookupAt(candidate);
			if (found != null) return found;
			const lastSlash = candidate.lastIndexOf('/');
			if (lastSlash <= 0) break;
			candidate = candidate.substring(0, lastSlash);
		}

		// Last shot: the bare root.
		return this._lookupAt('/');
	}

	private _lookupAt(cleanPath: string): string | null {
		// Prefer exact-match registered paths first.
		const exact = this.routeMap.get(cleanPath);
		if (
			exact?.activityDescription != null &&
			exact.activityDescription !== ''
		) {
			return this.substituteParams(exact.activityDescription, [], []);
		}

		// Walk matching patterns longest-first. The first matching route
		// that declares a description wins; matches without one fall
		// through so an annotated parent still labels its sub-routes.
		const entries = Array.from(this.routeMap.entries()).sort(
			(a, b) => b[0].length - a[0].length,
		);
		for (const [, info] of entries) {
			if (info.pattern == null) continue;
			const match = info.pattern.exec(cleanPath);
			if (match == null) continue;
			if (info.activityDescription != null && info.activityDescription !== '') {
				return this.substituteParams(
					info.activityDescription,
					info.paramNames ?? [],
					match.slice(1),
				);
			}
		}

		return null;
	}

	private substituteParams(
		template: string,
		paramNames: string[],
		paramValues: string[],
	): string {
		if (paramNames.length === 0) return template;

		return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
			const idx = paramNames.indexOf(name);
			if (idx === -1) return whole;
			const value = paramValues[idx];
			if (value == null || value === '') return whole;
			try {
				return decodeURIComponent(value);
			} catch {
				return value;
			}
		});
	}

	setCurrentPath(path: string): void {
		this.currentPath = path;
	}

	// Find route info for a given path
	findRouteForPath(path: string): RouteInfo | null {
		// First try exact match
		const exactMatch = this.routeMap.get(path);
		if (exactMatch) {
			return exactMatch;
		}

		// Then try pattern matching
		for (const [_, routeInfo] of this.routeMap.entries()) {
			if (routeInfo.pattern && routeInfo.pattern.test(path)) {
				return routeInfo;
			}
		}

		return null;
	}

	// Get outlet routes for a parent route
	getOutletRoutes(parentRouteName: string): OutletRoute[] {
		const routes = this.outletRoutes.get(parentRouteName) || [];

		// Filter out routes that are not visible. A tab is dropped when
		// either its visible() returns false, or its requiredPermissions
		// list is set and the current user has none of those actions.
		// requiredPermissions uses OR semantics to match ProtectedRoute.
		return routes.filter((route) => {
			if (route.visible && !route.visible()) {
				return false;
			}

			if (
				route.requiredPermissions != null &&
				route.requiredPermissions.length > 0
			) {
				const actions = route.requiredPermissions.map((p) => p.action);
				if (!PermissionService.canAny(actions)) {
					return false;
				}
			}

			return true;
		});
	}

	// Navigation item management
	getNavigationItems(): NavigationItem[] {
		if (this.globalNavigation) {
			return this.globalNavigation;
		}

		const items: NavigationItem[] = [];

		this.getActiveModules().forEach((module) => {
			if (module.navigation) {
				// Handle both NavigationConfig and NavigationItem[] formats
				const navItems = Array.isArray(module.navigation)
					? module.navigation
					: module.navigation.items;

				navItems.forEach((navItem) => {
					items.push({
						...navItem,
						moduleName: module.name,
					});
				});
			}
		});

		// Sort by order if specified
		return items.sort((a, b) => {
			const orderA = a.order ?? 999;
			const orderB = b.order ?? 999;
			return orderA - orderB;
		});
	}

	setGlobalNavigation(items: NavigationItem[]): void {
		this.globalNavigation = items;
		this.notifyListeners();
	}

	// Footer item management
	getFooterItems(): FooterItem[] {
		const items: FooterItem[] = [];
		const seenNames = new Set<string>();

		this.getActiveModules().forEach((module) => {
			// Check both footer and footerItems properties
			const footerItems = module.footer || module.footerItems || [];

			footerItems.forEach((footerItem) => {
				// Deduplicate by name if provided
				if (footerItem.name) {
					if (seenNames.has(footerItem.name)) {
						return;
					}
					seenNames.add(footerItem.name);
				}
				items.push(footerItem);
			});
		});

		return items;
	}

	// Module route generation for React Router v6 with nested routes - returns configuration objects
	getModuleRoutes(): RouteConfig[] {
		const routes: RouteConfig[] = [];

		const processRoutes = (
			moduleRoutes: Route[],
			parentPath = '',
		): RouteConfig[] =>
			moduleRoutes.flatMap((route): RouteConfig[] => {
				// All routes should now be relative (no leading slash) from modules
				const routePath = route.path;

				// For grouping routes (no component), flatten children to parent level
				if (!route.component && route.children) {
					// Pass children up with the accumulated parent path
					const childPath = parentPath
						? `${parentPath}/${routePath}`
						: routePath;
					return processRoutes(route.children, childPath);
				}

				// Handle outlet routes (detail pages with tabs)
				if (route.outlet && route.children && route.component) {
					const Component = route.component;
					const childRoutes: RouteConfig[] = buildOutletChildRoutes(
						route.children,
					);

					// Build the full path for this route
					const fullPath = parentPath
						? `${parentPath}/${routePath}`
						: routePath;

					return [
						{
							path: fullPath,
							element: React.createElement(Component),
							children: childRoutes,
							requiredPermissions: route.requiredPermissions,
						},
					];
				}

				// Regular route with component
				if (route.component) {
					const Component = route.component;
					const fullPath = parentPath
						? `${parentPath}/${routePath}`
						: routePath;

					const config: RouteConfig = {
						path: fullPath,
						element: React.createElement(Component),
						requiredPermissions: route.requiredPermissions,
					};

					// Handle default routes at any level
					if (route.default && route.path === '') {
						// For root level defaults, we need both index and empty path route
						return [
							{
								index: true,
								element: React.createElement(Component),
								requiredPermissions: route.requiredPermissions,
							},
							config,
						];
					}

					// Process non-outlet children if any
					if (route.children && !route.outlet) {
						config.children = processRoutes(route.children, fullPath);
					}

					return [config];
				}

				return [];
			});

		// Process each module's routes
		this.getActiveModules().forEach((module) => {
			if (module.routes) {
				routes.push(...processRoutes(module.routes));
			}
		});

		return routes;
	}

	// AI module support
	getAIModules(): Array<{ name: string; ai: NonNullable<ModuleConfig['ai']> }> {
		return this.getAllModules()
			.filter((module) => module.ai)
			.map((module) => ({ name: module.name, ai: module.ai! }));
	}

	getAIActionsForModule(moduleName: string): ModuleConfig['ai'] | undefined {
		return this.getModule(moduleName)?.ai;
	}

	// Listener support
	subscribe(listener: () => void): () => void {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	}

	private notifyListeners(): void {
		this.listeners.forEach((listener) => {
			try {
				listener();
			} catch (error) {
				// // Error: ModuleRegistry listener failed
			}
		});
	}

	// Initialization
	initialize(): void {
		if (this.initialized) {
			return;
		}

		this.initialized = true;
	}

	reset(): void {
		this.modules.clear();
		this.routeMap.clear();
		this.outletRoutes.clear();
		this.listeners.clear();
		this.globalNavigation = null;
		this.initialized = false;
	}

	// Debug helpers
	getRouteMap(): Map<string, RouteInfo> {
		return new Map(this.routeMap);
	}

	getModuleNames(): string[] {
		return Array.from(this.modules.keys());
	}
}

export const ModuleRegistry = new ModuleRegistryClass();
